import React from "react";
import { Checkbox, CircularProgress, Dialog, FormControlLabel, WithStyles, withStyles } from "@material-ui/core";
import Button from "@components/common/button/Button";
import IconButton from "@components/common/iconButton/iconButton.component";
import { connect } from "react-redux";
import CloseIcon from "@material-ui/icons/CloseRounded";
import BackIcon from "@material-ui/icons/ArrowBackRounded";
import CheckIcon from "@material-ui/icons/CheckCircleOutlineRounded";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutlineRounded";
import { IFamilyMember } from "@app-types/websocket/family.types";
import { FamilyMemberType } from "@services/family/family.constants";
import * as ExternalCalendarActions from "@services/externalCalendar/externalCalendar.actions";
import { externalCalendarStyles } from "./externalCalender.style";
import { calendarSource, calendarViewers, isCalendarUrl, normalizeCalendarUrl } from "./externalCalendar.utils";
import ImgAvatar from "@components/common/avatar/ImgAvatar";
import { getSelectedFamilyResource } from "@services/user/user.utils";
import TranslateNew from "@translate/TranslateNew";
import { parseICSFromURL } from "@helpers/parseICS";
import { IExternalCalendar } from "@app-types/websocket/externalCalender.types";

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof externalCalendarStyles> {
  open: boolean;
  close: () => void;
  onBack?: () => void;
  onSaved?: () => void;
  family: IFamilyMember[];
  myFamilyId: number;
  saveExternalCal: (payload: IExternalCalendar) => void;
  updateExternalCal: (payload: IExternalCalendar) => void;
  deleteExternalCal: (id: number) => void;
  externalCalendars: IExternalCalendar[];
  editCalendarId: number | "NEW";
  userId: number;
}

interface IState {
  url: string;
  name: string;
  calendarPerson?: number;
  sharedWith: number[];
  step: "link" | "details";
  checking: boolean;
  checkedUrl?: string;
  eventCount?: number;
  error?: string;
  nameError: boolean;
  confirmDelete: boolean;
}

export class ExternalCalendarEdit extends React.Component<IProps, IState> {
  private requestId = 0;
  private nameRef = React.createRef<HTMLInputElement>();

  constructor(props: IProps) {
    super(props);
    this.state = this.createState(props);
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.editCalendarId !== this.props.editCalendarId || (!prevProps.open && this.props.open)) {
      this.requestId++;
      this.setState(this.createState(this.props));
    } else if (prevProps.open && !this.props.open) {
      this.requestId++;
    }
  }

  public componentWillUnmount() {
    this.requestId++;
  }

  public render() {
    const { open, classes, editCalendarId, onBack } = this.props;
    const { step, checking, error, confirmDelete } = this.state;
    const isNew = editCalendarId === "NEW";
    const owned = this.isOwner();
    const title = isNew ? tn.lang.externalCalAdd : owned ? tn.lang.externalCalEdit : tn.lang.externalCalDetails;
    return <Dialog open={open} onClose={this.handleClose} fullWidth={true} maxWidth="sm"
      classes={{ paper: classes.paper }} aria-labelledby="external-calendar-edit-title">
      <div className={classes.header}>
        <div className={classes.heading}><h2 id="external-calendar-edit-title" className={classes.title}>{title}</h2></div>
        <IconButton onClick={this.handleClose} aria-label={tn.lang.close}><CloseIcon /></IconButton>
      </div>
      <div className={classes.content}>
        {isNew && <ol className={classes.steps} aria-label={tn.lang.externalCalAdd}>
          {[tn.lang.externalCalStepLink, tn.lang.externalCalStepDetails].map((label, index) => {
            const active = (step === "link" ? 0 : 1) === index;
            return <li key={label} className={`${classes.step} ${active ? classes.activeStep : ""}`}
              aria-current={active ? "step" : undefined}>
              <span className={`${classes.stepNumber} ${active ? classes.activeStepNumber : ""}`}>{index + 1}</span>{label}
            </li>;
          })}
        </ol>}
        {isNew && step === "link" ? this.renderLinkStep() : owned ? this.renderDetails() : this.renderSharedCalendar()}
        {error && <p id="calendar-link-error" className={classes.error} role="alert">{error}</p>}
        {!isNew && <div className={classes.dangerArea}>
          {confirmDelete ? <div className={classes.confirmation} role="alert" aria-labelledby="calendar-remove-title">
            <h3 id="calendar-remove-title" className={classes.sectionTitle}>{tn.lang.externalCalRemoveTitle(this.state.name)}</h3>
            <p className={classes.hint}>{owned ? tn.lang.externalCalRemoveDescription : tn.lang.externalCalLeaveDescription}</p>
            <div className={classes.confirmationActions}>
              <Button className={classes.textButton} onClick={() => this.setState({ confirmDelete: false })}>{tn.lang.cancel}</Button>
              <Button className={`${classes.textButton} ${classes.danger}`} onClick={this.handleDelete}>
                {owned ? tn.lang.externalCalRemove : tn.lang.externalCalLeave}
              </Button>
            </div>
          </div> : <Button className={`${classes.textButton} ${classes.danger}`} startIcon={<DeleteIcon />}
            disabled={checking} onClick={() => this.setState({ confirmDelete: true })}>
            {owned ? tn.lang.externalCalRemove : tn.lang.externalCalLeave}
          </Button>}
        </div>}
      </div>
      <div className={classes.footer}>
        <Button className={classes.textButton} startIcon={<BackIcon />} onClick={this.handleBack}>
          {isNew && step === "details" ? tn.lang.back : onBack ? tn.lang.externalCalBackToList : tn.lang.cancel}
        </Button>
        {owned && <Button className={classes.actionButton} variant="contained" color="primary"
          disabled={checking || confirmDelete || !this.state.url.trim() || (!isNew && !this.hasUpdate())}
          startIcon={checking ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={this.handleContinue}>
          {checking ? tn.lang.externalCalChecking : isNew && step === "link" ? tn.lang.externalCalCheckLink
            : isNew ? tn.lang.externalCalAdd : tn.lang.save}
        </Button>}
      </div>
    </Dialog>;
  }

  private renderLinkStep() {
    const { classes } = this.props;
    return <>
      <h3 className={classes.sectionTitle}>{tn.lang.externalCalLinkTitle}</h3>
      <p className={classes.hint}>{tn.lang.externalCalLinkDescription}</p>
      {this.renderLinkField(true)}
      <details className={classes.help}>
        <summary>{tn.lang.externalCalFindLink}</summary>
        <p><strong>Google</strong>{tn.lang.externalCalGoogleHelp}</p>
        <p><strong>Apple / iCloud</strong>{tn.lang.externalCalAppleHelp}</p>
        <p><strong>Outlook</strong>{tn.lang.externalCalOutlookHelp}</p>
        <p><strong>{tn.lang.externalCalOtherProvider}</strong>{tn.lang.externalCalOtherHelp}</p>
      </details>
    </>;
  }

  private renderLinkField(autoFocus = false) {
    const { classes } = this.props;
    return <div className={classes.field}>
      <label htmlFor="external-calendar-url" className={classes.label}>{tn.lang.externalCalLinkLabel}</label>
      <input id="external-calendar-url" className={classes.input} type="text" inputMode="url" autoComplete="off"
        autoCapitalize="none" spellCheck={false} autoFocus={autoFocus} value={this.state.url}
        aria-invalid={!!this.state.error} aria-describedby={`calendar-link-hint${this.state.error ? " calendar-link-error" : ""}`}
        placeholder="https://…" onChange={(event) => {
          this.requestId++;
          this.setState({ url: event.target.value, checkedUrl: undefined, eventCount: undefined, error: undefined, checking: false });
        }} />
      <p id="calendar-link-hint" className={classes.hint}>{tn.lang.externalCalLinkHint}</p>
    </div>;
  }

  private renderDetails() {
    const { classes, family, userId, editCalendarId } = this.props;
    const { eventCount, nameError } = this.state;
    return <>
      {eventCount !== undefined && <div className={classes.success} role="status">
        <CheckIcon /><div>
          <span>{eventCount === 0 ? tn.lang.externalCalConnectedEmpty : tn.lang.externalCalConnected(eventCount)}</span>
          <p className={classes.source}>{tn.lang.externalCalSource}: {calendarSource(this.state.url)}</p>
        </div>
      </div>}
      {editCalendarId !== "NEW" && this.renderLinkField()}
      <div className={classes.field}>
        <label htmlFor="external-calendar-name" className={classes.label}>{tn.lang.externalCalName}</label>
        <input id="external-calendar-name" ref={this.nameRef} className={classes.input} type="text" value={this.state.name}
          maxLength={50} required={true} aria-invalid={nameError} aria-describedby="calendar-name-hint"
          onChange={(event) => this.setState({ name: event.target.value, nameError: false })} />
        <p id="calendar-name-hint" className={nameError ? classes.error : classes.hint} role={nameError ? "alert" : undefined}>
          {nameError ? tn.lang.externalCalNameRequired : tn.lang.externalCalNameHint}
        </p>
      </div>
      <div className={classes.field}>
        <label htmlFor="external-calendar-person" className={classes.label}>{tn.lang.externalCalPerson}</label>
        <select id="external-calendar-person" className={classes.input} value={this.state.calendarPerson || ""}
          aria-describedby="calendar-person-hint" onChange={(event) => this.setState({
            calendarPerson: event.target.value ? Number(event.target.value) : undefined,
          })}>
          <option value="">{tn.lang.externalCalNoPerson}</option>
          {this.state.calendarPerson && !family.some((member) => member.id === this.state.calendarPerson)
            && <option value={this.state.calendarPerson}>{tn.lang.externalCalMember}</option>}
          {family.map((member) => <option key={member.id} value={member.id}>{member.firstName}</option>)}
        </select>
        <p id="calendar-person-hint" className={classes.hint}>{tn.lang.externalCalPersonHint}</p>
      </div>
      <fieldset className={classes.sharing} aria-describedby="calendar-sharing-hint">
        <legend className={classes.label}>{tn.lang.externalCalSharingTitle}</legend>
        <p id="calendar-sharing-hint" className={classes.hint}>{tn.lang.externalCalSharingHint}</p>
        <div className={classes.sharingOptions}>
          <FormControlLabel className={classes.sharingOption} control={<Checkbox color="primary" checked={true} disabled={true} />}
            label={<span className={classes.sharingName}>{tn.lang.externalCalAlwaysVisible}</span>} />
          {family.filter((member) => member.id !== userId).map((member) =>
            <FormControlLabel key={member.id} className={classes.sharingOption}
              control={<Checkbox color="primary" checked={this.state.sharedWith.includes(member.id)}
                onChange={(_, checked) => this.setState((state) => ({
                  sharedWith: checked ? [...state.sharedWith, member.id] : state.sharedWith.filter((id) => id !== member.id),
                }))} />}
              label={<span className={classes.sharingName}>
                <ImgAvatar color={member.color} imgName={member.avatar}
                  imgUrl={member.imageAvatar ? member.avatarURL : undefined} style={{ width: 28, height: 28 }} />
                {member.firstName}
              </span>} />)}
        </div>
      </fieldset>
    </>;
  }

  private renderSharedCalendar() {
    const { classes, family, userId } = this.props;
    const original = this.original();
    if (!original) { return null; }
    const creator = family.find((member) => member.id === original.creator);
    const person = family.find((member) => member.id === original.calendarPerson);
    return <>
      <h3 className={classes.sectionTitle}>{original.calendarName}</h3>
      <p className={classes.source}>{tn.lang.externalCalSource}: {calendarSource(original.url)}</p>
      <dl className={classes.metadata}>
        <dt className={classes.metaLabel}>{tn.lang.externalCalPerson}</dt>
        <dd className={classes.metaValue}>
          {person ? person.firstName : original.calendarPerson ? tn.lang.externalCalMember : tn.lang.externalCalNoPerson}
        </dd>
        <dt className={classes.metaLabel}>{tn.lang.externalCalVisibleTo}</dt>
        <dd className={classes.metaValue}>{calendarViewers(original).map((id) => {
          const member = family.find((m) => m.id === id);
          return id === userId ? tn.lang.externalCalYou : member ? member.firstName : tn.lang.externalCalMember;
        }).join(", ")}</dd>
      </dl>
      <p className={classes.description}>{tn.lang.externalCalReadOnly(creator ? creator.firstName : tn.lang.externalCalMember)}</p>
      <div className={classes.field}><div className={classes.info}><InfoIcon /><span>{tn.lang.externalCalHowItWorks}</span></div></div>
    </>;
  }

  private createState(props: IProps): IState {
    const calendar = props.externalCalendars.find((cal) => cal.id === props.editCalendarId);
    return {
      url: calendar ? calendar.url : "", name: calendar ? calendar.calendarName : "",
      calendarPerson: calendar ? calendar.calendarPerson || undefined : undefined,
      sharedWith: calendar ? [...(calendar.sharedWith || [])] : [],
      step: props.editCalendarId === "NEW" ? "link" : "details",
      checking: false, nameError: false, confirmDelete: false,
      checkedUrl: undefined, eventCount: undefined, error: undefined,
    };
  }

  private original = () => this.props.externalCalendars.find((cal) => cal.id === this.props.editCalendarId);
  private isOwner = () => this.props.editCalendarId === "NEW" || this.original()?.creator === this.props.userId;

  private hasUpdate() {
    const original = this.original();
    if (!original) { return false; }
    return original.calendarName !== this.state.name.trim()
      || normalizeCalendarUrl(original.url) !== normalizeCalendarUrl(this.state.url)
      || (original.calendarPerson || undefined) !== this.state.calendarPerson
      || [...(original.sharedWith || [])].sort().join(",") !== [...this.state.sharedWith].sort().join(",");
  }

  private handleContinue = async () => {
    if (!this.isOwner() || this.state.checking || this.state.confirmDelete) { return; }
    const isNew = this.props.editCalendarId === "NEW";
    if ((!isNew || this.state.step === "details") && !this.state.name.trim()) {
      this.setState({ nameError: true });
      if (this.nameRef.current) { this.nameRef.current.focus(); }
      return;
    }
    const url = normalizeCalendarUrl(this.state.url);
    if (!isCalendarUrl(url)) {
      this.setState({ error: tn.lang.externalCalInvalidLink });
      return;
    }
    const original = this.original();
    const needsCheck = this.state.checkedUrl !== url && (isNew || !original || normalizeCalendarUrl(original.url) !== url);
    if (needsCheck) {
      const requestId = ++this.requestId;
      this.setState({ checking: true, error: undefined });
      try {
        const events = await parseICSFromURL(url, true, true);
        if (requestId !== this.requestId) { return; }
        this.setState({ checkedUrl: url, eventCount: events.length, checking: false });
      } catch (_) {
        if (requestId === this.requestId) { this.setState({ checking: false, error: tn.lang.externalCalCannotRead }); }
        return;
      }
    }
    if (isNew && this.state.step === "link") {
      this.setState({ step: "details", url }, () => { if (this.nameRef.current) { this.nameRef.current.focus(); } });
      return;
    }
    const payload: IExternalCalendar = {
      calendarName: this.state.name.trim(), url, calendarPerson: this.state.calendarPerson,
      sharedWith: this.state.sharedWith, familyId: this.props.myFamilyId,
    };
    if (isNew) {
      this.props.saveExternalCal(payload);
    } else if (original) {
      this.props.updateExternalCal({ ...payload, id: original.id, creator: original.creator });
    }
    (this.props.onSaved || this.props.close)();
  };

  private handleDelete = () => {
    if (typeof this.props.editCalendarId === "number" && this.state.confirmDelete) {
      this.props.deleteExternalCal(this.props.editCalendarId);
      (this.props.onSaved || this.props.close)();
    }
  };

  private handleBack = () => {
    this.requestId++;
    if (this.props.editCalendarId === "NEW" && this.state.step === "details") {
      this.setState({ step: "link", error: undefined, checking: false });
    } else {
      (this.props.onBack || this.handleClose)();
    }
  };

  private handleClose = () => {
    this.requestId++;
    this.props.close();
  };
}

const mapStateToProps = (state: any) => {
  const myFamilyId = getSelectedFamilyResource(state.user?.session)?.id || 0;
  return {
    family: (state.family.family || []).filter((member: IFamilyMember) =>
      member.role === FamilyMemberType.CHILD || member.role === FamilyMemberType.PARENT || member.role === FamilyMemberType.VIEWER),
    externalCalendars: (state.externalCalendar.externalCalendars || []).filter((cal: IExternalCalendar) => cal.familyId === myFamilyId),
    userId: state.user.session.data.id, myFamilyId,
  };
};

const mapDispatchToProps = {
  saveExternalCal: ExternalCalendarActions.saveExternalCalendar,
  updateExternalCal: ExternalCalendarActions.updateExternalCalendar,
  deleteExternalCal: ExternalCalendarActions.deleteExternalCalendar,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(externalCalendarStyles)(ExternalCalendarEdit));



// WEBPACK FOOTER //
// ./src/components/calendar2/externalCalendar/ExternalCalendarEdit.tsx